import { format } from "date-fns";
import { observer } from "mobx-react";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useLanguage } from "hooks/useSelectedLanguage";

import { TeacherModals } from "constants/modals-constants";

import { InvitationBundleType } from "models/product/StudentInvitation";

import type { TableRow } from "components/SortableTable/SortableTable.model";

import { StyledTableContainer } from "pages/AdminEnvironment/AdminPage/subpages/UsersPage/components/StudentsTable/StudentsTable.styled";
import { CssIcon } from "styles/helpers/layout";
import { StyledBundleTable, StyledButton } from "./InvitationBundleList.styled";

import { EmailInviteBundleDetailsModal } from "../../components/EmailInviteBundleDetailsModal/EmailInviteBundleDetailsModal";
import { InviteBundleStatusTag } from "../../components/InviteBundleStatusTag/InviteBundleStatusTag";
import { LinkInviteDetailsModal } from "../../components/LinkInviteDetailsModal/LinkInviteDetailsModal";

const TYPE_FA_ICON_MAP: Record<InvitationBundleType, string> = {
  [InvitationBundleType.EMAIL]: "fas fa-envelope",
  [InvitationBundleType.LINK]: "far fa-link",
};

export const InvitationBundleList: React.FC = observer(() => {
  const { t } = useTranslation("teacher-dashboard");
  const { openModal } = useModal();
  const { currentLanguage } = useLanguage();
  const { loading, invitationBundleList, fetchInvitationBundleList } = useProduct();

  const handleViewBundle = useCallback(
    (bundleId: string, bundleType: InvitationBundleType) => {
      if (bundleType === InvitationBundleType.EMAIL) {
        openModal(TeacherModals.ViewEmailInviteBundleDetails, {
          emailInviteBundleId: bundleId,
        });
      } else if (bundleType === InvitationBundleType.LINK) {
        openModal(TeacherModals.ViewLinkInviteDetails, {
          linkInviteId: bundleId,
        });
      }
    },
    [openModal],
  );

  useEffect(() => {
    fetchInvitationBundleList();
  }, [fetchInvitationBundleList]);

  const tableData = useMemo<TableRow[]>(
    () =>
      invitationBundleList.inviteBundles.map((bundle, index) => {
        const {
          type,
          products,
          groupName,
          licenseExpirationDate,
          totalInvites,
          totalAcceptedInvites,
        } = bundle;

        return {
          id: `bundle-${index}-row`,
          dataCy: "bundle-row",
          cells: [
            {
              id: `bundle-${index}-type`,
              dataCy: "bundle-type",
              content: (
                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <CssIcon iconName={TYPE_FA_ICON_MAP[type]} />
                  {t(`studentLicense.invitationBundleList.types.${type}`)}
                </span>
              ),
            },
            {
              id: `bundle-${index}-product`,
              dataCy: "bundle-product",
              content: products.join(", "),
            },
            {
              id: `bundle-${index}-group`,
              dataCy: "bundle-group",
              content: groupName ?? "-",
            },
            {
              id: `bundle-${index}-expiry-date`,
              dataCy: "bundle-expiry-date",
              content: format(new Date(licenseExpirationDate), "dd-MM-yyyy"),
            },
            {
              id: `bundle-${index}-usage`,
              dataCy: "bundle-usage",
              content: `${totalAcceptedInvites} / ${totalInvites}`,
            },
            {
              id: `bundle-${index}-status`,
              dataCy: "bundle-status",
              content: (
                <InviteBundleStatusTag
                  currentUsage={totalAcceptedInvites}
                  maxUsage={totalInvites}
                  expirationDate={licenseExpirationDate}
                />
              ),
            },
            {
              id: `bundle-${index}-action`,
              dataCy: "bundle-action",
              content: (
                <StyledButton
                  dataCy="bundle-view-button"
                  disabled={loading}
                  variant="text"
                  onClick={() => handleViewBundle(bundle.id, bundle.type)}
                >
                  <CssIcon iconName="fas fa-eye" style={{ marginRight: "0.4rem" }} />
                  {t("studentLicense.invitationBundleList.actions.view")}
                </StyledButton>
              ),
            },
          ],
        };
      }),
    [invitationBundleList.inviteBundles, loading, t, currentLanguage, handleViewBundle],
  );

  return (
    <>
      <StyledTableContainer data-cy="invite-bundle-list">
        <StyledBundleTable
          flexStartCellHeaders
          headerCells={[
            {
              id: "bundle-type-column",
              title: t("studentLicense.invitationBundleList.columns.type"),
              size: "1fr",
            },
            {
              id: "bundle-product-column",
              title: t("studentLicense.invitationBundleList.columns.product"),
              size: "1fr",
            },
            {
              id: "bundle-group-column",
              title: t("studentLicense.invitationBundleList.columns.group"),
              size: "1fr",
            },
            {
              id: "bundle-expiry-date-column",
              title: t("studentLicense.invitationBundleList.columns.expiryDate"),
              size: "1fr",
            },
            {
              id: "bundle-usage-column",
              title: t("studentLicense.invitationBundleList.columns.usage"),
              size: "1fr",
            },
            {
              id: "bundle-status-column",
              title: t("studentLicense.invitationBundleList.columns.status"),
              size: "1fr",
            },
            {
              id: "bundle-action-column",
              title: t("studentLicense.invitationBundleList.columns.action"),
              size: "auto",
            },
          ]}
          rows={tableData}
        />
      </StyledTableContainer>
      {/* -- Invite bundle details -- */}
      <EmailInviteBundleDetailsModal />
      <LinkInviteDetailsModal />
    </>
  );
}) as React.FC;
